import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Table,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';

import * as api from '../planningApi';
import { validateCrudeCapacity } from '../validation';

// "Add Crude / Condensat" popup for one expanded cargo row.
//
// Rows come LIVE from GET .../LookupImplementationCrude (contract section 9),
// filtered server-side by the cargo's CDU + Cargo Category. Each row shows the
// crude name and its Technical Limit (the hard ceiling for Max Capacity — the
// server rejects `maxCapacity > technicalLimit` with 422). Max Capacity, Remark,
// Is Via CIB Only and both Blending values are user inputs for the AddCargoCrude
// call (section 10); the master lookup does not carry them.
//
// UX mengikuti desain acuan: baris tampil read-only (Max Capacity di-prefill
// dari Technical Limit), tombol pensil membuka edit inline per baris (input
// ber-highlight amber), pensil berubah jadi centang untuk selesai.
const emptyInputs = {
  maxCapacity: '',
  remarkMaxCapacity: '',
  isViaCibOnly: false,
  blending1: '',
  blending2: '',
};

// Highlight amber pada input mode edit (mengikuti desain acuan).
const editInputStyle = {
  backgroundColor: '#fff8e1',
  borderColor: '#f7c948',
};

// Remark default sebuah baris (status mapping asli dari master). Field remark
// bersifat OVERRIDE: bila user membiarkannya kosong, nilai default inilah yang
// dikirim & tersimpan — bukan string kosong.
const defaultRemark = row =>
  row.technicalLimit != null
    ? `Ter-mapping CDU (Batas: ${row.technicalLimit} BPSD)`
    : 'Ter-mapping CDU';

// Header tabel navy + teks putih (desain acuan). Diterapkan per-sel <th>
// karena Bootstrap mengecat background sel via CSS variable — background di
// <thead> saja akan tertimpa. Sticky supaya header tetap terlihat saat scroll.
const headerCellStyle = {
  backgroundColor: '#1e3a56',
  color: '#ffffff',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const AddCrudeModal = ({
  show,
  planningId,
  cargo,
  existingIds = [],
  onClose,
  onOk,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [inputs, setInputs] = useState({}); // implementationCrudeId -> emptyInputs
  const [editingId, setEditingId] = useState(null); // baris yang sedang diedit
  const [search, setSearch] = useState('');

  // Fetch fresh candidates each time the popup opens for this cargo.
  useEffect(() => {
    if (!show || !cargo?.id) return;
    setLoading(true);
    setChecked({});
    setEditingId(null);
    setSearch('');
    api
      .lookupImplementationCrude(planningId, cargo.id)
      .then(items => {
        setRows(
          items.map(it => ({
            implementationCrudeId: it.id,
            crudeName: it.text,
            technicalLimit: it.technicalLimit ?? null,
          }))
        );
        // Prefill Max Capacity dari Technical Limit supaya baris read-only
        // langsung menampilkan angka (dan nilainya pasti lolos validasi ≤ limit).
        setInputs(
          Object.fromEntries(
            items.map(it => [
              it.id,
              {
                ...emptyInputs,
                maxCapacity:
                  it.technicalLimit != null ? String(it.technicalLimit) : '',
              },
            ])
          )
        );
      })
      .catch(error => {
        toast.error(`Failed to load Crude / Condensat: ${error}`);
        setRows([]);
        setInputs({});
      })
      .finally(() => setLoading(false));
  }, [show, planningId, cargo?.id]);

  // Crudes already taken over into this cargo cannot be added twice.
  const available = useMemo(
    () => rows.filter(r => !existingIds.includes(r.implementationCrudeId)),
    [rows, existingIds]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter(r => r.crudeName.toLowerCase().includes(q));
  }, [available, search]);

  const getInputs = id => inputs[id] || emptyInputs;
  const patchInputs = (id, patch) =>
    setInputs(prev => ({ ...prev, [id]: { ...getInputs(id), ...patch } }));

  const selectedCount = available.filter(
    r => checked[r.implementationCrudeId]
  ).length;

  const handleOk = () => {
    const selected = available.filter(r => checked[r.implementationCrudeId]);
    if (!selected.length) {
      toast.error('Select at least one Crude / Condensat');
      return;
    }
    // Validate each selected row's Max Capacity against its Technical Limit.
    const payload = [];
    for (const r of selected) {
      const inp = getInputs(r.implementationCrudeId);
      const errors = validateCrudeCapacity({
        maxCapacity: inp.maxCapacity,
        technicalLimit: r.technicalLimit,
      });
      if (errors.length) {
        toast.error(`${r.crudeName}: ${errors.join(', ')}`);
        return;
      }
      payload.push({
        ...r,
        ...inp,
        // Remark = override: kosong → kirim status mapping asli dari master,
        // sehingga nilai yang tersimpan tidak pernah kosong.
        remarkMaxCapacity: inp.remarkMaxCapacity?.trim()
          ? inp.remarkMaxCapacity
          : defaultRemark(r),
      });
    }
    onOk(payload);
  };

  const allChecked =
    filtered.length > 0 &&
    filtered.every(r => checked[r.implementationCrudeId]);

  const toggleAll = () =>
    setChecked(prev => {
      const next = { ...prev };
      filtered.forEach(r => {
        next[r.implementationCrudeId] = !allChecked;
      });
      return next;
    });

  return (
    <Modal isOpen={show} toggle={onClose} size='xl' centered>
      <ModalHeader toggle={onClose}>
        Add Crude/Condensate (Master Data)
        {cargo?.cargoType && (
          <span className='text-muted fs-13 fw-normal ms-2'>
            — {cargo.cargoType}
          </span>
        )}
      </ModalHeader>

      <ModalBody>
        {/* Banner target delivery (Plant • CDU) mengikuti desain acuan. */}
        {(cargo?.plant || cargo?.unit) && (
          <div className='alert alert-info d-flex align-items-center py-2 mb-3'>
            <i className='ri-building-2-line fs-16 me-2'></i>
            <div className='fs-13'>
              <strong>Target Delivery:</strong>{' '}
              <span className='badge bg-info-subtle text-info border border-info'>
                {[cargo.plant, cargo.unit].filter(Boolean).join(' • ')}
              </span>
              <span className='text-muted ms-2'>
                Daftar Crude ditarik dari Master Data yang termapping pada Plant
                &amp; CDU target.
              </span>
            </div>
          </div>
        )}

        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
          <Input
            type='text'
            style={{ maxWidth: 420 }}
            placeholder='Cari nama crude atau catatan spesifikasi...'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className='text-muted fs-13'>
            Menampilkan <strong>{filtered.length}</strong> dari{' '}
            <strong>{available.length}</strong> Master Crude
          </span>
        </div>

        <div className='table-responsive' style={{ maxHeight: 420 }}>
          <Table className='table-sm align-middle mb-0'>
            <thead>
              {/* Warna diletakkan per-sel <th>: Bootstrap mengecat background
                  sel lewat CSS variable, jadi background di <thead> saja
                  tertimpa dan teks putih jadi tak terbaca. Sticky agar header
                  tetap terlihat saat daftar panjang di-scroll. */}
              <tr>
                <th style={{ ...headerCellStyle, width: '36px' }}>
                  <Input
                    type='checkbox'
                    className='form-check-input'
                    checked={allChecked}
                    onChange={toggleAll}
                    aria-label='Select all'
                  />
                </th>
                <th style={{ ...headerCellStyle, width: '44px' }}></th>
                <th style={headerCellStyle}>Crude / Condensate Name</th>
                <th style={{ ...headerCellStyle, width: '130px' }}>
                  Max Capacity (BPSD)
                </th>
                <th style={{ ...headerCellStyle, width: '220px' }}>
                  Status Mapping &amp; Remark
                </th>
                <th
                  style={{
                    ...headerCellStyle,
                    width: '70px',
                    textAlign: 'center',
                  }}
                >
                  CIB Only
                </th>
                <th style={{ ...headerCellStyle, width: '110px' }}>
                  % Blending Crude 1
                </th>
                <th style={{ ...headerCellStyle, width: '110px' }}>
                  % Blending Crude 2
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className='text-center text-muted py-4'>
                    <Spinner size='sm' className='me-2' />
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className='text-center text-muted py-4'>
                    No Crude / Condensat available for this cargo
                  </td>
                </tr>
              ) : (
                filtered.map(row => {
                  const id = row.implementationCrudeId;
                  const inp = getInputs(id);
                  const isEditing = editingId === id;
                  // Remark bersifat override — kosong berarti nilai default
                  // (status mapping asli) yang tampil DAN yang dikirim saat OK.
                  const remarkDisplay =
                    inp.remarkMaxCapacity || defaultRemark(row);
                  return (
                    <tr key={id}>
                      <td className='text-center'>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          checked={!!checked[id]}
                          onChange={e =>
                            setChecked(prev => ({
                              ...prev,
                              [id]: e.target.checked,
                            }))
                          }
                          aria-label={`Select ${row.crudeName}`}
                        />
                      </td>
                      <td className='text-center'>
                        {/* Pensil = buka edit inline; centang = selesai. */}
                        <button
                          type='button'
                          className={`btn btn-sm btn-icon ${
                            isEditing
                              ? 'btn-soft-success'
                              : 'btn-ghost-secondary'
                          }`}
                          title={isEditing ? 'Done' : 'Edit values'}
                          onClick={() => setEditingId(isEditing ? null : id)}
                        >
                          <i
                            className={`${
                              isEditing ? 'ri-check-line' : 'ri-pencil-line'
                            } align-bottom`}
                          ></i>
                        </button>
                      </td>
                      <td>
                        <span className='fw-semibold d-block'>
                          {row.crudeName}
                        </span>
                        <span className='badge bg-success-subtle text-success mt-1'>
                          <i className='ri-checkbox-circle-line me-1'></i>
                          Mapped CDU
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            type='number'
                            bsSize='sm'
                            style={editInputStyle}
                            value={inp.maxCapacity}
                            onChange={e =>
                              patchInputs(id, { maxCapacity: e.target.value })
                            }
                          />
                        ) : (
                          <span className='fw-medium'>
                            {inp.maxCapacity !== ''
                              ? `${inp.maxCapacity} BPSD`
                              : '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            type='text'
                            bsSize='sm'
                            style={editInputStyle}
                            placeholder={remarkDisplay}
                            value={inp.remarkMaxCapacity}
                            onChange={e =>
                              patchInputs(id, {
                                remarkMaxCapacity: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <span className='text-muted fs-13'>
                            {remarkDisplay}
                          </span>
                        )}
                      </td>
                      <td className='text-center'>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          disabled={!isEditing}
                          checked={!!inp.isViaCibOnly}
                          onChange={e =>
                            patchInputs(id, { isViaCibOnly: e.target.checked })
                          }
                          aria-label='Is Via CIB Only'
                        />
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            type='number'
                            bsSize='sm'
                            style={editInputStyle}
                            value={inp.blending1}
                            onChange={e =>
                              patchInputs(id, { blending1: e.target.value })
                            }
                          />
                        ) : (
                          <span>
                            {inp.blending1 !== '' ? `${inp.blending1}%` : '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            type='number'
                            bsSize='sm'
                            style={editInputStyle}
                            value={inp.blending2}
                            onChange={e =>
                              patchInputs(id, { blending2: e.target.value })
                            }
                          />
                        ) : (
                          <span>
                            {inp.blending2 !== '' ? `${inp.blending2}%` : '-'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </ModalBody>

      <ModalFooter className='justify-content-between'>
        <span className='text-muted fs-13'>
          Telah dipilih: <strong>{selectedCount}</strong> Crude/Condensate
        </span>
        <div className='d-flex gap-2'>
          <Button color='primary' onClick={handleOk} disabled={loading}>
            Tambahkan Pilihan ({selectedCount})
          </Button>
          <Button color='light' onClick={onClose}>
            Batal
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AddCrudeModal;
